import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Recruitment",
  "Software Development",
  "AI Chatbot Development",
  "Web Design & Development",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "Software Development",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Got it. Our team will reply within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        service: "Software Development",
        phone: "",
        message: "",
      });
    } catch (err) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-mono-label mb-4">§ 06 — Contact</div>
          <h2 className="font-display text-5xl md:text-7xl">
            Let's
            <br />
            talk.
          </h2>
          <p className="mt-6 text-[color:var(--fis-ink-soft)] max-w-sm leading-relaxed">
            Tell us what you're building or who you're hiring. We'll reply
            within 24 hours with next steps, not a sales deck.
          </p>

          <div className="mt-10 space-y-4 text-sm">
            <a
              href="mailto:contact@frontlineintelligencesystem.com"
              data-testid="contact-email"
              className="flex items-center gap-3 fis-link-underline w-fit"
            >
              <Mail className="w-4 h-4" />
              contact@frontlineintelligencesystem.com
            </a>
            <a
              href="tel:+918470915123"
              data-testid="contact-phone-in"
              className="flex items-center gap-3 fis-link-underline w-fit"
            >
              <Phone className="w-4 h-4" />
              +91 84709 15123 <span className="font-mono-label ml-1">IN</span>
            </a>
            <a
              href="tel:+16093059720"
              data-testid="contact-phone-us"
              className="flex items-center gap-3 fis-link-underline w-fit"
            >
              <Phone className="w-4 h-4" />
              +1 609-305-9720 <span className="font-mono-label ml-1">US</span>
            </a>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
              <span>C-27, EFC, 2nd Floor, Trapezoid IT Park,<br />Sector 62, Noida, UP 201301</span>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 md:col-start-6 space-y-6"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input
              data-testid="contact-name"
              className="fis-input"
              placeholder="Name *"
              value={form.name}
              onChange={onChange("name")}
            />
            <input
              data-testid="contact-email-input"
              className="fis-input"
              placeholder="Email *"
              type="email"
              value={form.email}
              onChange={onChange("email")}
            />
            <input
              data-testid="contact-company"
              className="fis-input"
              placeholder="Company"
              value={form.company}
              onChange={onChange("company")}
            />
            <input
              data-testid="contact-phone"
              className="fis-input"
              placeholder="Phone"
              value={form.phone}
              onChange={onChange("phone")}
            />
          </div>
          <div>
            <label className="font-mono-label block mb-3">Service</label>
            <div className="flex flex-wrap gap-2">
              {services.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setForm({ ...form, service: s })}
                  data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-4 py-2 text-sm border transition-all ${
                    form.service === s
                      ? "bg-[color:var(--fis-ink)] text-white border-transparent"
                      : "border-black/15 hover:border-[color:var(--fis-ink)]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <textarea
            data-testid="contact-message"
            className="fis-input resize-none"
            rows={5}
            placeholder="What are you working on? *"
            value={form.message}
            onChange={onChange("message")}
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="fis-btn fis-btn-primary disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send message"}
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
